import axios from "axios";
import {
  isCacheable,
  storeResponse,
  getCachedResponse,
  clearClockDataCache,
} from "@/utils/clockDataCache";
import { clearQueue } from "@/utils/offlineQueue";

const OFFLINE_OWNER_KEY = "oppoOfflineOwner";

// Offline data (queued clock events + cached PHI) belongs to one user. On a
// user switch, wipe it: the cache must never leak another user's clients,
// and another user's queued events must never upload under this session.
// A same-user re-login keeps the queue — those are real clock events.
function claimOfflineDataOwnership(userId) {
  try {
    const previous = localStorage.getItem(OFFLINE_OWNER_KEY);
    if (previous && String(previous) !== String(userId)) {
      clearQueue();
      clearClockDataCache();
    }
    localStorage.setItem(OFFLINE_OWNER_KEY, String(userId));
  } catch {
    /* ignore */
  }
}

export class CommonAPIService {
  constructor(router) {
    this.router = router;

    this.client = axios.create({
      baseURL: process.env.VUE_APP_API_URL,
      timeout: 300000,
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem("jwtToken");
      if (token) config.headers.Authorization = token;
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Skip token refresh for login/auth endpoints - let them handle their own errors
        const isAuthEndpoint = originalRequest.url?.includes('/api/login') ||
                               originalRequest.url?.includes('/forgot-password') ||
                               originalRequest.url?.includes('/reset-password');

        if (isAuthEndpoint) {
          return Promise.reject(error);
        }

        // If 401 error and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Try to refresh the token
            const token = localStorage.getItem("jwtToken");
            if (!token) {
              throw new Error("No token available");
            }

            const refreshResponse = await axios.post(
              `${process.env.VUE_APP_API_URL}/api/login/refresh-token`,
              {},
              {
                headers: {
                  Authorization: token,
                },
              }
            );

            if (refreshResponse.data.success && refreshResponse.data.signUpToken) {
              // Store the new token
              const newToken = refreshResponse.data.signUpToken;
              localStorage.setItem("jwtToken", newToken);

              // Update the original request with the new token
              originalRequest.headers.Authorization = newToken;

              // Retry the original request
              return this.client(originalRequest);
            } else {
              throw new Error("Token refresh failed");
            }
          } catch (refreshError) {
            // The refresh endpoint returned a hard failure. The token is unusable — remove it.
            try {
              const authService = require("@/services/authService").default;
              authService.logout(
                "Your session has expired. Please log in again.",
                { removeToken: true }
              );
            } catch (e) {
              // Fallback if authService is unavailable in this context
              localStorage.removeItem("jwtToken");
              try {
                const { useLoginStore } = require("src/stores/loginStore");
                useLoginStore().$reset();
              } catch (storeErr) { /* ignore */ }
              this.router.push({
                name: "Login",
                query: { message: "Your session has expired. Please log in again." }
              });
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );
  }
  getCall(url, params = {}) {
    // Read-through offline cache for the clock-flow endpoints: successful
    // responses are saved; on a network failure the saved copy is served so
    // clock-in/out still works offline (see utils/clockDataCache.js).
    if (!isCacheable(url)) {
      return this.client.get(url, { params }).then((r) => r.data);
    }
    return this.client
      .get(url, { params })
      .then((r) => {
        storeResponse(url, params, r.data);
        return r.data;
      })
      .catch(async (error) => {
        const isNetworkFailure = !navigator.onLine || (error.isAxiosError && !error.response);
        if (isNetworkFailure) {
          const cached = await getCachedResponse(url, params);
          if (cached !== null) return cached;
        }
        throw error;
      });
  }
  postCall(url, data = {}) {
    return this.client.post(url, data).then((r) => r.data);
  }
  putCall(url, data = {}) {
    return this.client.put(url, data).then((r) => r.data);
  }
  deleteCall(url) {
    return this.client.delete(url).then((r) => r.data);
  }
  postFormDataCall(url, formData) {
    return this.client
      .post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((r) => r.data);
  }
  getBlobCall(url, params = {}) {
    return this.client.get(url, { params, responseType: "blob" }).then((r) => r.data);
  }
  login(credentials, store, errors) {
    return this.client
      .post("/api/login", credentials)
      .then(({ data }) => {
        const { user, signUpToken, requirePasswordChange, impersonating, adminEmail } = data;
        claimOfflineDataOwnership(user?.id);
        store.setUser(user);
        store.setLoggedIn(true);
        localStorage.setItem("jwtToken", signUpToken);

        // Handle impersonation if present in response
        if (impersonating) {
          store.setImpersonating(true, adminEmail);
        } else {
          // Clear any previous impersonation state on normal login
          store.clearImpersonation();
        }

        // Initialize auth service to handle idle timeout
        try {
          const authService = require("@/services/authService").default;
          authService.init();
        } catch (e) {
          // AuthService might not be available in all contexts
        }
        return { user, signUpToken, requirePasswordChange };
      })
      .catch((e) => {
        errors.push(e);
        // Return the specific error message from the API response
        const message = e.response?.data?.message || "Login Failure";
        return { success: false, message };
      });
  }
  forgotPassword(data) {
    return this.client
      .post("/api/login/forgot-password", data)
      .then((response) => {
        return { success: true, ...response.data };
      })
      .catch((error) => {
        return {
          success: false,
          message: error.response?.data?.message || "Failed to send reset email",
        };
      });
  }
  resetPassword(data) {
    return this.client
      .post("/api/login/reset-password", data)
      .then((response) => {
        return { success: true, ...response.data };
      })
      .catch((error) => {
        return {
          success: false,
          message: error.response?.data?.message || "Failed to reset password",
        };
      });
  }
  changePassword(data) {
    return this.client.post("/api/login/change-password", data);
  }
  adminChangePassword(data) {
    return this.client.post("/api/login/admin-change-password", data);
  }
}

export default CommonAPIService;
